import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formsApi } from '../services/formsApi';
import type { FormQuery, CreateFormRequest, UpdateFormRequest } from '@troia-v3/types';
import { toast } from 'sonner';

export const useForms = (query?: FormQuery) => {
  return useQuery({
    queryKey: ['forms', query],
    queryFn: () => formsApi.getAll(query),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useForm = (id?: string) => {
  return useQuery({
    queryKey: ['form', id],
    queryFn: () => formsApi.getById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};

/** "N aplicações usam este modelo" — só no modo edição do construtor. */
export const useFormUsage = (id?: string, enabled = true) => {
  return useQuery({
    queryKey: ['form-usage', id],
    queryFn: () => formsApi.getUsage(id!),
    enabled: enabled && !!id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFormRequest) => formsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      // `forms-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // "Modelo" de Novo checklist fica com a lista velha por até 5min.
      queryClient.invalidateQueries({ queryKey: ['forms-dropdown'] });
      toast.success('Formulário criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar formulário');
    },
  });
};

export const useUpdateForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFormRequest }) => formsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      // `forms-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // "Modelo" de Novo checklist fica com a lista velha por até 5min.
      queryClient.invalidateQueries({ queryKey: ['forms-dropdown'] });
      queryClient.invalidateQueries({ queryKey: ['form', variables.id] });
      toast.success('Formulário atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar formulário');
    },
  });
};

export const useDeleteForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      // `forms-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // "Modelo" de Novo checklist fica com a lista velha por até 5min.
      queryClient.invalidateQueries({ queryKey: ['forms-dropdown'] });
      toast.success('Formulário excluído com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao excluir formulário');
    },
  });
};

export const useDuplicateForm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsApi.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      // `forms-dropdown` é chave IRMÃ (não prefixo) — sem esta linha o Select
      // "Modelo" de Novo checklist fica com a lista velha por até 5min.
      queryClient.invalidateQueries({ queryKey: ['forms-dropdown'] });
      toast.success('Formulário duplicado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao duplicar formulário');
    },
  });
};
